import type { ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useStore } from '../stores/store';
import { cacheRead } from '../api/client';
import { ErrorBoundary } from './ErrorBoundary';
import {
  IconHoy, IconPartidos, IconResumen, IconZonas, IconLlave, IconHistorico, IconUso, IconMenu,
} from './NavIcons';

const Shell = styled.div`
  min-height: 100%;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  padding-bottom: 72px; /* deja lugar a la bottom nav */
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.accentText};
`;

const Brand = styled.div`
  font-weight: 800;
  letter-spacing: 0.5px;
  font-size: 1.1rem;
`;

const Actions = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.16);
  color: inherit;
  border: none;
  border-radius: 999px;
  padding: 6px 8px 6px 14px;
`;

const UserName = styled.span`
  font-weight: 700;
  font-size: 0.95rem;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Main = styled.main`
  padding: 12px 16px;
`;

const Nav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  a {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    font-size: 0.78rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  a.active {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 700;
  }
  a .ico {
    display: block;
    margin: 0 auto 3px;
    height: 30px;
  }
  a .ico svg { display: block; margin: 0 auto; }
`;

const HOY = { to: '/', Icon: IconHoy, label: 'Hoy' };
// Orden pedido: Hoy · Partidos · Resumen · Zonas · Llave · Histórico.
const BASE_TABS = [
  { to: '/fixtures', Icon: IconPartidos, label: 'Partidos' },
  { to: '/resumen', Icon: IconResumen, label: 'Resumen' },
  { to: '/groups', Icon: IconZonas, label: 'Zonas' },
  { to: '/bracket', Icon: IconLlave, label: 'Llave' },
  { to: '/historico', Icon: IconHistorico, label: 'Histórico' },
];

const USO = { to: '/uso', Icon: IconUso, label: 'Uso' };

export function Layout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const nick = useStore((s) => s.nick);
  const adminKey = useStore((s) => s.adminKey);

  // Mientras dura el Mundial: "Hoy" primero. Al terminar (remaining 0): se oculta "Hoy"
  // y "Resumen" pasa a ser principal. "Uso" solo aparece si se desbloqueó el acceso.
  const t = cacheRead<{ remaining: number }>('/tournament');
  const finished = t != null && t.remaining === 0;
  const TABS = [
    ...(finished ? [] : [HOY]),
    ...BASE_TABS,
    ...(adminKey ? [USO] : []),
  ];

  return (
    <Shell>
      <Header>
        <Brand>VamoVamo!</Brand>
        <Actions onClick={() => navigate('/preferencias')} aria-label="Menú y preferencias">
          <UserName>{nick || 'Invitado'}</UserName>
          <IconMenu size={22} />
        </Actions>
      </Header>
      <Main>
        <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
      </Main>
      <Nav>
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.to === '/'}>
            <span className="ico">
              <t.Icon size={30} />
            </span>
            {t.label}
          </NavLink>
        ))}
      </Nav>
    </Shell>
  );
}
